import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { featureFlagsService } from '../api/featureFlagsService';
import { trackEvent } from '../api/analyticsService';

// Flags are refetched on window focus, but at most this often — admin changes reach the
// client within ~1 min of refocus (the API itself caches flags for 30s).
const REFRESH_INTERVAL_MS = 60_000;
const STORAGE_KEY = 'ww_ff';
const EXPOSED_KEY = 'ww_ff_exposed';

interface FeatureFlagsContextType {
    enabled: Set<string>;
    loaded: boolean;
    /** Who the flags were loaded for — exposure de-duplication is per user. */
    userId: number | undefined;
    refresh: () => Promise<void>;
}

const FeatureFlagsContext = createContext<FeatureFlagsContextType>({
    enabled: new Set(),
    loaded: false,
    userId: undefined,
    refresh: async () => {},
});

// Last-known flags per user, so flagged UI doesn't flash on reload while the fetch is in flight.
function readCached(userId: number | undefined): string[] {
    if (!userId) return [];
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : null;
        return parsed?.u === userId && Array.isArray(parsed.f) ? parsed.f : [];
    } catch {
        return [];
    }
}

function writeCached(userId: number, ids: string[]): void {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ u: userId, f: ids }));
    } catch {
        // storage unavailable — flags still work, just without the warm start
    }
}

export const FeatureFlagsProvider = ({ children }: { children: ReactNode }) => {
    const auth = useAuth();
    const userId: number | undefined = auth?.user?.id;
    const isPro = auth?.isPro ?? false;
    const [enabled, setEnabled] = useState<Set<string>>(() => new Set(readCached(userId)));
    // The user whose fetch last completed. `loaded` is derived from it during render, so on a
    // direct switch from one account to another it is false in the very first render for the
    // new user — child effects (useFeature's exposure tracking) run before this provider's
    // effects, so resetting a `loaded` state in an effect would come too late and record the
    // new user as exposed to the previous user's flags.
    const [loadedForUserId, setLoadedForUserId] = useState<number | undefined>(undefined);
    const loaded = userId !== undefined && loadedForUserId === userId;
    const lastFetch = useRef(0);
    // Sequence number of the latest refresh — only its response may be applied. Refreshes can
    // overlap (login, then isPro flipping when the subscription loads; logout → login as someone
    // else), and an older response landing last must not overwrite newer flags.
    const latestRequest = useRef(0);

    const refresh = useCallback(async () => {
        const requestId = ++latestRequest.current;
        if (!userId) {
            setEnabled(new Set());
            setLoadedForUserId(undefined);
            return;
        }
        lastFetch.current = Date.now();
        try {
            const ids = await featureFlagsService.getEnabled();
            if (requestId !== latestRequest.current) return;
            setEnabled(new Set(ids));
            writeCached(userId, ids);
        } catch {
            // keep the last known flags on a transient failure
        } finally {
            if (requestId === latestRequest.current) setLoadedForUserId(userId);
        }
    }, [userId]);

    // Refetch on login/logout and when the plan changes (flags can target free/pro).
    useEffect(() => {
        setEnabled(new Set(readCached(userId)));
        refresh();
    }, [userId, isPro, refresh]);

    useEffect(() => {
        const onFocus = () => {
            if (Date.now() - lastFetch.current > REFRESH_INTERVAL_MS) refresh();
        };
        window.addEventListener('focus', onFocus);
        return () => window.removeEventListener('focus', onFocus);
    }, [refresh]);

    const value = useMemo(() => ({ enabled, loaded, userId, refresh }), [enabled, loaded, userId, refresh]);
    return <FeatureFlagsContext.Provider value={value}>{children}</FeatureFlagsContext.Provider>;
};

// Sends feature_exposed once per browser session per (user, flag, state), for both ON and OFF
// users, so the admin Stats tab can compare the two groups. Keyed by user so a second account
// signing in on the same tab is still counted.
function trackExposure(userId: number, flagId: string, isOn: boolean): void {
    const key = `${userId}:${flagId}:${isOn ? 1 : 0}`;
    try {
        const seen: string[] = JSON.parse(sessionStorage.getItem(EXPOSED_KEY) || '[]');
        if (seen.includes(key)) return;
        sessionStorage.setItem(EXPOSED_KEY, JSON.stringify([...seen, key]));
    } catch {
        // sessionStorage unavailable — fall through and track anyway
    }
    trackEvent('feature_exposed', { f: flagId, enabled: isOn, app: 'web' });
}

/** Whether the feature flag with this public id is on for the current user. */
export function useFeature(flagId: string): boolean {
    const { enabled, loaded, userId } = useContext(FeatureFlagsContext);
    const isOn = enabled.has(flagId);
    useEffect(() => {
        if (loaded && userId) trackExposure(userId, flagId, isOn);
    }, [flagId, isOn, loaded, userId]);
    return isOn;
}

export function useFeatureFlags(): FeatureFlagsContextType {
    return useContext(FeatureFlagsContext);
}

/** Renders children only when the flag is on, otherwise the fallback. */
export const Feature = ({ flag, children, fallback = null }: { flag: string; children: ReactNode; fallback?: ReactNode }) => {
    const isOn = useFeature(flag);
    return <>{isOn ? children : fallback}</>;
};

/**
 * Route guard: renders children when the flag is on, otherwise redirects to `redirectTo`.
 * Renders nothing while flags are still loading (and the flag isn't already on from the
 * cached warm start), so a direct visit doesn't bounce before the fetch resolves. Use inside
 * the auth guard — flags never load for signed-out users.
 */
export const FeatureRoute = ({ flag, children, redirectTo = '/dashboard' }: { flag: string; children: ReactNode; redirectTo?: string }) => {
    const { loaded } = useContext(FeatureFlagsContext);
    const isOn = useFeature(flag);
    if (isOn) return <>{children}</>;
    if (!loaded) return null;
    return <Navigate to={redirectTo} replace />;
};
